import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { parsePhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input'

import { users } from '../../actions'
import { parseJSON } from '../helpers/commonHelpers'

export const ACCOUNT_DETAILS_SYNC_SUCCESS_EVENT = 'woaAccountDetailsSyncSuccess'
export const ACCOUNT_DETAILS_SYNC_SUCCESS_STORAGE_KEY = 'woaAccountDetailsSyncSuccess'

const useAccountDetailsSync = () => {
  const userUuid = useSelector(state => state.users.user?.uuid)
  const userAccount = useSelector(state => state.users.userAccount)
  const userAccountRef = useRef(userAccount)
  const dispatch = useDispatch()

  useEffect(() => {
    userAccountRef.current = userAccount
  }, [userAccount])

  const checkAndUpdateAccountDetails = useCallback(async (sessionDetails) => {
    const currentUserAccount = userAccountRef.current
    const newDetails = JSON.parse(JSON.stringify(currentUserAccount))
    let hasChanges = false
    const newTelephone = verifyPhoneNumber(sessionDetails?.mobilePhone, currentUserAccount.telephone)
    const customFields = parseJSON(currentUserAccount.fax) || {}

    if (newTelephone) {
      const { country } = parsePhoneNumber(newTelephone)
      customFields.countryCode = country
      Object.keys(customFields).forEach(key => {
        if (key !== 'countryCode' && key !== 'sms' && key !== 'oet') {
          delete customFields[key]
        }
      })
      newDetails.telephone = newTelephone
      hasChanges = true
    }

    // Outbid Email Timestamp - if missing, then set it and enable outbid emails
    if (!customFields?.oet && !currentUserAccount.isOutbidEmailPermitted) {
      newDetails.isOutbidEmailPermitted = true
      customFields.oet = Date.now()
      customFields.sms = true
      hasChanges = true
    }

    // ! Email is not stored in BLX state, so this part is commented out for now
    // if (compareFields(sessionDetails.email, userAccount.email)) {
    //   newDetails.email = sessionDetails.email
    //   newDetails.email = sessionDetails.email
    //   console.log('>>>> Email has changed:', sessionDetails.email, userAccount.email)
    // }

    if (compareFields(sessionDetails.addressDetails.addressLine1, currentUserAccount.addressDetails.addressLine1)) {
      newDetails.addressDetails.addressLine1 = sessionDetails.addressDetails.addressLine1
      hasChanges = true
    }

    if (compareFields(sessionDetails.addressDetails.addressLine2, currentUserAccount.addressDetails.addressLine2)) {
      newDetails.addressDetails.addressLine2 = sessionDetails.addressDetails.addressLine2
      hasChanges = true
    }

    if (compareFields(sessionDetails.addressDetails.city, currentUserAccount.addressDetails.city)) {
      newDetails.addressDetails.city = sessionDetails.addressDetails.city
      hasChanges = true
    }

    if (compareFields(sessionDetails.addressDetails.state, currentUserAccount.addressDetails.state)) {
      newDetails.addressDetails.state = sessionDetails.addressDetails.state
      hasChanges = true
    }

    if (compareFields(sessionDetails.addressDetails.postalCode, currentUserAccount.addressDetails.postalCode)) {
      newDetails.addressDetails.postalCode = sessionDetails.addressDetails.postalCode
      hasChanges = true
    }

    if (compareFields(sessionDetails.addressDetails.countryId, currentUserAccount.addressDetails.countryId)) {
      newDetails.addressDetails.countryId = sessionDetails.addressDetails.countryId
      hasChanges = true
    }
    console.log('>>>> Session sync check completed, hasChanges:', hasChanges, 'newDetails:', newDetails, 'currentUserAccount:', currentUserAccount)
    if (hasChanges) {
      newDetails.fax = JSON.stringify(customFields)
      await dispatch(users.patchAccount(userUuid, newDetails))
      return true
    }

    return false
  }, [userUuid, dispatch])

  const verifyPhoneNumber = (newPhoneNumber, userAccountPhoneNumber) => {
    try {
      let previousPhoneNumber = userAccountPhoneNumber
      let currentPhoneNumber = newPhoneNumber.toString()
      if (previousPhoneNumber && !previousPhoneNumber.startsWith('+')) {
        previousPhoneNumber = '+' + previousPhoneNumber
      }
      if (currentPhoneNumber && !currentPhoneNumber.startsWith('+')) {
        let parsedCurrentNumber = parsePhoneNumber('+' + currentPhoneNumber)
        if (!parsedCurrentNumber && currentPhoneNumber.startsWith('04')) {
          parsedCurrentNumber = parsePhoneNumber('+' + currentPhoneNumber.replace(/^0/, '61'))
          currentPhoneNumber = parsedCurrentNumber?.number
        } else {
          currentPhoneNumber = parsedCurrentNumber?.number
        }
      }
      if (!currentPhoneNumber) {
        console.error('Invalid new mobile number provided:', newPhoneNumber)
        return false
      }
      const formattedPreviousPhoneNumber = formatPhoneNumberIntl(previousPhoneNumber)
      const formattedCurrentPhoneNumber = formatPhoneNumberIntl(currentPhoneNumber)
      if (formattedPreviousPhoneNumber !== formattedCurrentPhoneNumber) {
        console.log('Mobile number is new.')
        return formattedCurrentPhoneNumber
      } else {
        console.log('Mobile number is already up-to-date.')
        return false
      }
    } catch (error) {
      console.error('Error checking or updating mobile number:', error)
      return false
    }
  }

  const compareFields = (newField, currentField) => {
    let result = false
    try {
      result = (newField || '').toString().replace(/\s+/g, '').toLowerCase() !== (currentField || '').toString().replace(/\s+/g, '').toLowerCase()
    } catch {
      console.log('>>>> Error comparing fields:', { newField, currentField })
    }
    return result
  }

  return { checkAndUpdateAccountDetails }
}

export default useAccountDetailsSync
